import DialogProvider from "@components/dialog_provider"
import { getCustomers } from "@domain/customer/list"
import {
  getReembedKnowledgeSourcesToContextualRagJobQuery,
  ReembedKnowledgeSourcesToContextualRagJob,
  startReembedKnowledgeSourcesToContextualRagMutation
} from "@domain/scripts"
import useTranslate from "@hooks/use_translate"
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography
} from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import dayjs from "dayjs"
import { useEffect, useMemo, useState } from "react"

type ReembedKnowledgeSourcesToContextualRagDialogProps = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

const LAST_REEMBED_JOB_ID_STORAGE_KEY =
  "scripts:reembed-contextual-rag:last-job-id"

const RUNNING_JOB_STATUSES = new Set(["queued", "running"])

const STATUS_LABELS: Record<string, string> = {
  queued: "Queued",
  running: "Running",
  completed: "Completed",
  failed: "Failed"
}

export default function ReembedKnowledgeSourcesToContextualRagDialog({
  isOpen,
  setIsOpen
}: ReembedKnowledgeSourcesToContextualRagDialogProps) {
  const { translate } = useTranslate()
  const [selectedCustomerId, setSelectedCustomerId] = useState("")
  const [startLoading, setStartLoading] = useState(false)
  const [jobId, setJobId] = useState<string | null>(null)
  const [jobState, setJobState] =
    useState<ReembedKnowledgeSourcesToContextualRagJob | null>(null)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const storedJobId = localStorage.getItem(LAST_REEMBED_JOB_ID_STORAGE_KEY)
    if (storedJobId) {
      setJobId(storedJobId)
    }
  }, [isOpen])

  const { data: customerResponse, isLoading: customersLoading } = useQuery({
    queryKey: ["scripts-reembed-knowledge-customers"],
    enabled: isOpen,
    queryFn: async () => {
      const response = await getCustomers({
        pageNumber: 1,
        pageSize: 2000
      })

      if (!response.isSuccess) {
        showErrorToast("Failed to load customers")
        return {
          docs: [],
          totalDocs: 0
        }
      }

      return response.data
    }
  })

  const customerOptions = useMemo(
    () =>
      (customerResponse?.docs ?? [])
        .filter((item) => item.customer?.appStatus === "Enabled")
        .map((item) => ({
          value: item.customer?._id || "",
          label: `${item.customer?.companyName || "Unknown"} (${item.customer?._id || ""})`
        })),
    [customerResponse?.docs]
  )

  const customerLabelMap = useMemo(
    () =>
      new Map(customerOptions.map((item) => [item.value, item.label] as const)),
    [customerOptions]
  )

  const {
    data: polledJobState,
    isLoading: jobLoading,
    refetch: refetchJob
  } = useQuery({
    queryKey: ["reembed-knowledge-sources-job", jobId],
    enabled: isOpen && !!jobId,
    queryFn: async () => {
      if (!jobId) {
        return null
      }

      const response =
        await getReembedKnowledgeSourcesToContextualRagJobQuery(jobId)
      if (!response.isSuccess) {
        showErrorToast(response.errorMessage ?? "Failed to load job status")
        return undefined
      }

      return response.data ?? null
    },
    refetchInterval: (query) => {
      const data = query.state.data

      if (!jobId) {
        return false
      }

      if (!data) {
        return 5000
      }

      return RUNNING_JOB_STATUSES.has(data.status) ? 5000 : false
    }
  })

  useEffect(() => {
    if (polledJobState !== undefined) {
      setJobState(polledJobState)
    }
  }, [polledJobState])

  useEffect(() => {
    if (!jobId || jobLoading || polledJobState !== null) {
      return
    }

    localStorage.removeItem(LAST_REEMBED_JOB_ID_STORAGE_KEY)
    setJobId(null)
    setJobState(null)
  }, [jobId, jobLoading, polledJobState])

  useEffect(() => {
    if (!jobState || RUNNING_JOB_STATUSES.has(jobState.status)) {
      return
    }

    localStorage.removeItem(LAST_REEMBED_JOB_ID_STORAGE_KEY)
  }, [jobState])

  const isProgressMode = !!jobId
  const isJobRunning =
    !!jobState && RUNNING_JOB_STATUSES.has(jobState.status as string)

  function resetToForm() {
    localStorage.removeItem(LAST_REEMBED_JOB_ID_STORAGE_KEY)
    setJobId(null)
    setJobState(null)
  }

  function resolveCustomerLabel(customerId?: string | null) {
    if (!customerId) {
      return "-"
    }

    return customerLabelMap.get(customerId) || customerId
  }

  function handleClose() {
    if (startLoading) {
      return
    }

    if (!isJobRunning) {
      resetToForm()
    }

    setIsOpen(false)
  }

  async function onSubmit() {
    setStartLoading(true)

    const response = await startReembedKnowledgeSourcesToContextualRagMutation(
      selectedCustomerId
        ? { customerIds: [selectedCustomerId] }
        : { allCustomers: true }
    )

    if (!response.isSuccess || !response.data) {
      showErrorToast(response.errorMessage ?? "Failed to start re-embed job")
      setStartLoading(false)
      return
    }

    localStorage.setItem(LAST_REEMBED_JOB_ID_STORAGE_KEY, response.data.jobId)
    setJobId(response.data.jobId)
    setJobState(response.data)
    showSuccessToast("Re-embed job started in background")
    setStartLoading(false)
    void refetchJob()
  }

  const scopeLabel = jobState
    ? jobState.allCustomers
      ? "All customers"
      : resolveCustomerLabel(jobState.targetCustomerIds[0])
    : selectedCustomerId
      ? resolveCustomerLabel(selectedCustomerId)
      : "All customers"

  return (
    <DialogProvider
      title="Re-embed Knowledge Sources to Contextual RAG"
      leftButton={{
        text: isProgressMode ? translate("close") : translate("cancel"),
        color: "secondary",
        onClick: handleClose,
        disabled: startLoading
      }}
      rightButton={
        isProgressMode
          ? isJobRunning
            ? undefined
            : {
                text: "New run",
                color: "primary",
                onClick: resetToForm,
                disabled: startLoading
              }
          : {
              text: translate("submit"),
              color: "primary",
              onClick: onSubmit,
              loading: startLoading,
              disabled: startLoading
            }
      }
      isOpen={isOpen}
      setClose={handleClose}
      size="medium"
    >
      <Stack gap={2}>
        {!isProgressMode ? (
          <>
            <Typography variant="body2" color="text.secondary">
              Leave customer empty to re-embed all customers in the background.
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="reembed-knowledge-customer-select-label">
                {translate("customer")}
              </InputLabel>
              <Select
                labelId="reembed-knowledge-customer-select-label"
                value={selectedCustomerId}
                label={translate("customer")}
                onChange={(event) =>
                  setSelectedCustomerId(String(event.target.value))
                }
                disabled={customersLoading || startLoading}
              >
                <MenuItem value="">All customers</MenuItem>
                {customerOptions.map((customer) => (
                  <MenuItem key={customer.value} value={customer.value}>
                    {customer.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        ) : (
          <>
            {!jobState && jobLoading ? (
              <Box
                sx={{
                  py: 4,
                  display: "flex",
                  justifyContent: "center"
                }}
              >
                <CircularProgress size={28} />
              </Box>
            ) : null}

            {jobState ? (
              <>
                <Alert
                  severity={
                    isJobRunning
                      ? "info"
                      : jobState.status === "failed"
                        ? "error"
                        : "success"
                  }
                >
                  Status: {STATUS_LABELS[jobState.status] || jobState.status}
                </Alert>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack gap={1}>
                    <Typography variant="body2">Scope: {scopeLabel}</Typography>
                    <Typography variant="body2">
                      Current customer:{" "}
                      {resolveCustomerLabel(jobState.currentCustomerId)}
                    </Typography>
                    <Typography variant="body2">
                      Completed / Failed / Remaining:{" "}
                      {jobState.completedCustomers} / {jobState.failedCustomers}{" "}
                      / {jobState.remainingCustomers}
                    </Typography>
                    <Typography variant="body2">
                      Last updated:{" "}
                      {dayjs(jobState.updatedAt).format("DD.MM.YYYY HH:mm:ss")}
                    </Typography>
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack gap={1}>
                    <Typography variant="subtitle2">Content summary</Typography>
                    <Typography variant="body2">
                      Processed knowledge sources:{" "}
                      {jobState.processedKnowledgeSources}
                    </Typography>
                    <Typography variant="body2">
                      Processed contents: {jobState.processedContents}
                    </Typography>
                    <Typography variant="body2">
                      Re-embedded contents: {jobState.reembeddedContents}
                    </Typography>
                    <Typography variant="body2">
                      Skipped contents: {jobState.skippedContents}
                    </Typography>
                    <Typography variant="body2">
                      Failed contents: {jobState.failedContents}
                    </Typography>
                  </Stack>
                </Paper>

                <Stack direction={{ xs: "column", md: "row" }} gap={2}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, flex: 1, minHeight: "12rem" }}
                  >
                    <Stack gap={1}>
                      <Typography variant="subtitle2">
                        Completed customers
                      </Typography>
                      {jobState.completedCustomerIds.length > 0 ? (
                        jobState.completedCustomerIds.map((customerId) => (
                          <Typography key={customerId} variant="body2">
                            {resolveCustomerLabel(customerId)}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No completed customer yet.
                        </Typography>
                      )}
                    </Stack>
                  </Paper>

                  <Paper
                    variant="outlined"
                    sx={{ p: 2, flex: 1, minHeight: "12rem" }}
                  >
                    <Stack gap={1}>
                      <Typography variant="subtitle2">
                        Failed customers
                      </Typography>
                      {jobState.failedCustomerIds.length > 0 ? (
                        jobState.failedCustomerIds.map((customerId) => (
                          <Typography key={customerId} variant="body2">
                            {resolveCustomerLabel(customerId)}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No failed customer.
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                </Stack>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack gap={1}>
                    <Typography variant="subtitle2">Failures</Typography>
                    {jobState.failures.length > 0 ? (
                      jobState.failures.slice(-10).map((failure, index) => (
                        <Typography
                          key={`${failure.customerId || "unknown"}-${index}`}
                          variant="body2"
                        >
                          {resolveCustomerLabel(failure.customerId)}:{" "}
                          {failure.reason || "-"}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No failure recorded.
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              </>
            ) : null}
          </>
        )}
      </Stack>
    </DialogProvider>
  )
}
